import APITemplate from '@api/APITemplate'
import { diffTimeMinutes } from '@utils/Format'
import store from '@state/store'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import con from '@services/connection'
import { origens as endpoint } from '@modules/crm/constants/URL'

class APICrmOrigem extends APITemplate {
  constructor() {
    super()

    this.MODULE = 'crm'
    this.reqCarregar = null

    // STORE KEYS
    this.KEY_DATA = `${this.MODULE}/origens`
    this.KEY_LOADING = `${this.MODULE}/origensLoading`
    this.KEY_LAST_UPDATE = `${this.MODULE}/origensLastUpdate`

    // STORE ACTIONS
    this.ACTION_SET_DATA = `${this.MODULE}/setOrigens`
    this.ACTION_ADD_DATA = `${this.MODULE}/addOrigem`
    this.ACTION_REMOVE_DATA = `${this.MODULE}/removeOrigem`
    this.ACTION_SET_LOADING = `${this.MODULE}/setOrigensLoading`
  }

  // ---------- HELPERS ----------
  shouldUseCache(forceUpdate) {
    if (forceUpdate) return false

    return !diffTimeMinutes(
      store.getters[this.KEY_LAST_UPDATE],
      MINUTES_TIMEOUT_REQUESTS
    )
  }

  setDataFromResponse(response) {
    if (response && response.status === 200) {
      const value = Array.isArray(response.value) ? response.value : []
      store.dispatch(this.ACTION_SET_DATA, value)
      return
    }

    store.dispatch(this.ACTION_SET_DATA, [])
  }

  async getEarlyResponse(forceUpdate) {
    if (this.reqCarregar) {
      const response = await this.reqCarregar
      return (
        response ||
        this.buildErrorResponse({
          message: 'Erro ao carregar origens.',
        })
      )
    }

    if (this.shouldUseCache(forceUpdate)) {
      return this.buildSuccessResponse({
        message: 'Dados carregados da memória',
        value: store.getters[this.KEY_DATA],
      })
    }

    if (store.getters[this.KEY_LOADING]) {
      return this.buildSuccessResponse({
        message: 'Requisição em andamento, dados carregados da memória',
        value: store.getters[this.KEY_DATA],
      })
    }

    return null
  }

  // ---------- REQUESTS ----------
  async carregar({ forceUpdate = false, applyLoading = true } = {}) {
    const earlyResponse = await this.getEarlyResponse(forceUpdate)
    if (earlyResponse) return earlyResponse

    if (applyLoading) store.dispatch(this.ACTION_SET_LOADING, true)

    this.reqCarregar = con.get(endpoint.base)
    const response = await this.reqCarregar

    this.setDataFromResponse(response)

    if (applyLoading) store.dispatch(this.ACTION_SET_LOADING, false)
    this.reqCarregar = null

    return (
      response ||
      this.buildErrorResponse({
        message: 'Erro ao carregar origens.',
      })
    )
  }

  async salvar({ item } = {}) {
    if (!item || typeof item !== 'object') {
      return this.buildErrorResponse({
        status: 400,
        message: 'Dados inválidos.',
      })
    }

    const { id, ...rest } = item

    const data = {
      data: JSON.stringify(rest),
    }

    const response = id
      ? await con.put(endpoint.byId(id), data)
      : await con.post(endpoint.base, data)

    if (response && response.status === 200) {
      const valor = Array.isArray(response.value)
        ? response.value[0]
        : response.value

      if (valor) {
        store.dispatch(this.ACTION_ADD_DATA, valor)
      }
    }

    return (
      response ||
      this.buildErrorResponse({
        message: 'Erro ao salvar origem.',
      })
    )
  }

  async remover({ id } = {}) {
    if (!id) {
      return this.buildErrorResponse({
        status: 404,
        message: 'Origem não informada.',
      })
    }

    const response = await con.delet(endpoint.byId(id))

    if (response && response.status === 200) {
      store.dispatch(this.ACTION_REMOVE_DATA, id)
    }

    return (
      response ||
      this.buildErrorResponse({
        message: 'Erro ao remover origem.',
      })
    )
  }

  async usage({ id } = {}) {
    if (!id) {
      return this.buildErrorResponse({
        status: 404,
        message: 'Origem não informada.',
      })
    }

    const response = await con.get(endpoint.usage(id))

    return (
      response ||
      this.buildErrorResponse({
        message: 'Erro ao consultar uso da origem.',
      })
    )
  }

  async listarNaoRegistradas() {
    const response = await con.get(endpoint.naoRegistradas)

    return (
      response ||
      this.buildErrorResponse({
        message: 'Erro ao carregar origens não registradas.',
      })
    )
  }

  async registrarExistente({ nome } = {}) {
    if (!nome || typeof nome !== 'string' || !nome.trim()) {
      return this.buildErrorResponse({
        status: 400,
        message: 'Nome da origem é obrigatório.',
      })
    }

    const data = {
      data: JSON.stringify({ nome: nome.trim() }),
    }

    const response = await con.post(endpoint.registrarExistente, data)

    return (
      response ||
      this.buildErrorResponse({
        message: 'Erro ao registrar origem.',
      })
    )
  }
}

const apiCrmOrigem = new APICrmOrigem()
export { apiCrmOrigem }
